import React from "react";
import { Box, Typography } from "@mui/material";
import { BarChartOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import color from "../../Constants/colors";
import { ProfileCard, UsageMeter } from "./ProfileSection";

interface AlanQuotaProps {
  alanQuotaData: {
    request_quota: number;
    requests_this_month: number;
    requests_total: number;
    token_quota: number;
    tokens_this_month: number;
    tokens_total: number;
  };
}

const formatNumber = (num: number): string =>
  new Intl.NumberFormat("de-DE").format(num);

// The backend sends -1 for an unlimited quota.
const quotaLimit = (quota: number) => (quota === -1 ? null : quota);

const TotalTile: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Box
    sx={{
      p: 2,
      borderRadius: "12px",
      backgroundColor: color.gray50,
      border: `1px solid ${color.gray100}`,
    }}
  >
    <Typography sx={{ fontSize: 12.5, color: color.gray500 }}>
      {label}
    </Typography>
    <Typography
      sx={{
        mt: 0.5,
        fontSize: 20,
        fontWeight: 700,
        lineHeight: "26px",
        color: color.primary_dark,
      }}
    >
      {value}
    </Typography>
  </Box>
);

const AlanQuota: React.FC<AlanQuotaProps> = ({ alanQuotaData }) => {
  const { t } = useTranslation("profile");

  return (
    <ProfileCard title={t("profile.alanTitle")} icon={<BarChartOutlined />}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: { xs: 2.5, sm: 3 },
        }}
      >
        <UsageMeter
          label={t("profile.requestsThisMonth")}
          value={formatNumber(alanQuotaData.requests_this_month)}
          used={alanQuotaData.requests_this_month}
          limit={quotaLimit(alanQuotaData.request_quota)}
        />
        <UsageMeter
          label={t("profile.tokensThisMonth")}
          value={formatNumber(alanQuotaData.tokens_this_month)}
          used={alanQuotaData.tokens_this_month}
          limit={quotaLimit(alanQuotaData.token_quota)}
        />
        <TotalTile
          label={t("profile.totalRequests")}
          value={formatNumber(alanQuotaData.requests_total)}
        />
        <TotalTile
          label={t("profile.totalTokens")}
          value={formatNumber(alanQuotaData.tokens_total)}
        />
      </Box>
    </ProfileCard>
  );
};

export default AlanQuota;
